import { CurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import { html } from "lit-html";
import { iconButton } from "Components/iconButton";
import { getRequestUrl } from "Services/common";
import { PUB_SUBS } from "Services/pub-sub-service/PubSub";
import {
	buildFlightSearchRequest,
	handleReloadSearch,
	navigateToFlightSearch,
	redirectToAmericanAirlinesLogin,
	savePreviousSearch,
} from "ComponentHelpers/FlightsTabHelper";
import { SearchClickedEventData } from "Services/logging/logger/types-and-interfaces/SearchClickedEvent";
import { TestIdDictionary } from "Services/test-ids/TestIdDictionary";
import { useEffectWithoutCleanup, useMemo } from "Shared/haunted/CustomHooks";
import { useLoader } from "Shared/haunted/useLoader";
import { useLogger } from "Services/logging/logger/useLogger";
import { useNewFlightDateSelector } from "./date-selector/useNewFlightDateSelector";
import { usePromoCodeBox } from "./promo-code-box/usePromoCodeBox";
import { usePassengerSelector } from "./passenger-selector/usePassengerSelector";
import { usePreviousSearch } from "./usePreviousSearch";
import { useRadioToggle } from "../../../useRadioToggle";
import { useReduxState } from "Shared/redux/useReduxState";
import { useStationSelector } from "./station-selector/useStationSelector";
import { useWindowSize } from "Shared/haunted/useWindowSize";
import classNames from "classnames";
import i18next from "i18next";

export interface Props {
	currentSearchManager: CurrentSearchManager;
	culture: string;
	dynamicSettings: DynamicSettings;
	loaderElemId: string;
}

const REDEMPTION_MILES_ID = "redemptionMiles";

export const useFlightsTab = (props: Props) => {
	const windowSize = useWindowSize();

	const { logEvent } = useLogger();

	const [userInfo] = useReduxState("userInfo");

	const previousSearch = usePreviousSearch();
	const [navitaireSettings] = useReduxState("navitaireSettings");

	const promoCodeBox = usePromoCodeBox({
		culture: props.culture,
		currentSearchManager: props.currentSearchManager,
		dynamicSettings: props.dynamicSettings,
	});

	const showReloadLink = useMemo(
		() =>
			Boolean(previousSearch.load()) &&
			(!props.currentSearchManager.currentSearch.useAmericanMiles || !userInfo?.AmericanAirlines?.IsMember),
		[props.currentSearchManager.currentSearch.useAmericanMiles, userInfo?.AmericanAirlines?.IsMember]
	);

	const radioToggle = useRadioToggle({
		isOn: !props.currentSearchManager.currentSearch.isOneWay,
		firstOptionText: i18next.t("Solo ida"),
		secondOptionText: i18next.t("Ida y vuelta"),
		onChange: (isOneWay: boolean) => {
			props.currentSearchManager.setIsOneWay(isOneWay);
			props.currentSearchManager.setReturnDate(
				props.currentSearchManager.currentSearch.isOneWay !== isOneWay
					? undefined
					: props.currentSearchManager.currentSearch.returnDate
			);
		},
	});

	const { startLoading, stopLoading } = useLoader(props.loaderElemId);

	const stationSelector = useStationSelector({
		currentSearchManager: props.currentSearchManager,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		startLoading,
		stopLoading,
	});

	const passengerSelector = usePassengerSelector({
		culture: props.culture,
		currentSearchManager: props.currentSearchManager,
	});

	const dateSelector = useNewFlightDateSelector({
		currentSearchManager: props.currentSearchManager,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		openPassengerSelector: passengerSelector.open,
		startLoading,
		stopLoading,
	});

	const isChileCompraUser = () => userInfo?.ChileCompra?.IsMember;
	const isCug2User = () => Boolean(userInfo?.OrganizationName);
	const isPeruCompraUser = () => userInfo?.PeruCompra?.IsMember || userInfo?.PeruCompra?.IsAdmin;

	const showRedemptionMiles = () =>
		window.JetSmart?.DynamicSettings?.IsAmericanOn && !isCug2User() && !isChileCompraUser() && !isPeruCompraUser();

	const handleFormReload = (e: MouseEvent) =>
		handleReloadSearch(
			e,
			previousSearch,
			props.currentSearchManager.reloadSearch,
			props.currentSearchManager.currentSearch.promoCodes
		);

	const isFormValid = () =>
		!!props.currentSearchManager.currentSearch.originCity &&
		!!props.currentSearchManager.currentSearch.destinationCity &&
		!!props.currentSearchManager.currentSearch.departureDate &&
		(props.currentSearchManager.currentSearch.isOneWay ||
			!!props.currentSearchManager.currentSearch.returnDate) &&
		props.currentSearchManager.currentSearch.passengers.adults +
			props.currentSearchManager.currentSearch.passengers.teens >
			0;

	const logSearchEvent = () => {
		logEvent<SearchClickedEventData>("search_clicked", {
			adults: props.currentSearchManager.currentSearch.passengers.adults,
			children: props.currentSearchManager.currentSearch.passengers.children,
			culture: props.culture,
			departureDate: props.currentSearchManager.currentSearch.departureDate,
			destinationCode: props.currentSearchManager.currentSearch.destinationCity.code,
			infants: props.currentSearchManager.currentSearch.passengers.infants,
			numberOfJourneys: props.currentSearchManager.currentSearch.isOneWay ? 1 : 2,
			market:
				props.currentSearchManager.currentSearch.destinationCity.countryCode !==
				props.currentSearchManager.currentSearch.originCity.countryCode
					? "INTER"
					: `DOM-${props.currentSearchManager.currentSearch.originCity.countryCode}`,
			originCode: props.currentSearchManager.currentSearch.originCity.code,
			preloadedPromoCodes: promoCodeBox.predefinedPromoCodes,
			promoCodes: props.currentSearchManager.currentSearch.promoCodes,
			returnDate: props.currentSearchManager.currentSearch.returnDate,
			teens: props.currentSearchManager.currentSearch.passengers.teens,
		});
	};

	const navigateToResults = () => {
		startLoading();

		const isRedemptionSearch =
			showRedemptionMiles() &&
			props.currentSearchManager.currentSearch.useAmericanMiles &&
			userInfo?.AmericanAirlines?.IsMember;

		savePreviousSearch(previousSearch, {
			currentSearch: props.currentSearchManager.currentSearch,
			selectedPriceInbound: dateSelector.selectedPriceInbound?.toString().trim() || "",
			selectedPriceOutbound: dateSelector.selectedPriceOutbound?.toString().trim() || "",
		});

		const request = buildFlightSearchRequest({
			adults: props.currentSearchManager.currentSearch.passengers.adults,
			children: props.currentSearchManager.currentSearch.passengers.children,
			culture: props.culture,
			currency: navitaireSettings.currency,
			departureDate: props.currentSearchManager.currentSearch.departureDate,
			destinationCode: props.currentSearchManager.currentSearch.destinationCity.code,
			infants: props.currentSearchManager.currentSearch.passengers.infants,
			isOneWay: props.currentSearchManager.currentSearch.isOneWay,
			isRedemptionSearch: Boolean(isRedemptionSearch),
			originCode: props.currentSearchManager.currentSearch.originCity.code,
			promoCodes: props.currentSearchManager.currentSearch.promoCodes,
			returnDate: props.currentSearchManager.currentSearch.returnDate,
			selectedPriceInbound: dateSelector.selectedPriceInbound?.toString().trim() || "",
			selectedPriceOutbound: dateSelector.selectedPriceOutbound?.toString().trim() || "",
			teens: props.currentSearchManager.currentSearch.passengers.teens,
		});

		navigateToFlightSearch(getRequestUrl(request), props.dynamicSettings);
	};

	const handleFormSubmit = async () => {
		if (!isFormValid()) return;

		logSearchEvent();

		if (
			props.currentSearchManager.currentSearch.promoCodes.length > 0 &&
			props.currentSearchManager.currentSearch.useAmericanMiles &&
			!userInfo?.AmericanAirlines?.IsMember
		) {
			PUB_SUBS.RedemptionNoPromoCodeModalOpened.publish();
			return;
		}

		if (props.currentSearchManager.currentSearch.useAmericanMiles && !userInfo?.AmericanAirlines?.IsMember) {
			await redirectToAmericanAirlinesLogin(props.culture, userInfo?.RoleCode);
			return;
		}

		navigateToResults();
	};

	const button = iconButton({
		dataTestId: TestIdDictionary.SubmitSearchButton,
		isAction: true,
		icon: html`<i class="jsh-circle-chevron-right1 text-base"></i>`,
		isDisabled: !isFormValid(),
		label: i18next.t("search-box-submit"),
		onClick: handleFormSubmit,
	});

	useEffectWithoutCleanup(() => {
		if (
			userInfo?.RoleCode &&
			!!props.currentSearchManager.currentSearch.useAmericanMiles !== !!userInfo.AmericanAirlines?.IsMember
		) {
			props.currentSearchManager.setUseAmericanMiles(userInfo?.AmericanAirlines?.IsMember);
		}
	}, [userInfo?.RoleCode, userInfo?.AmericanAirlines?.IsMember]);

	const reloadLinkTemplate = () =>
		showReloadLink
			? html`
					<div class="m-0 flex items-center justify-center px-0 pb-0 pt-2 text-center">
						<span
							class="cursor-pointer text-sm font-bold leading-none text-n-blue underline hover:text-brand-primary"
							@click=${handleFormReload}
						>
							&#8635;&nbsp;${i18next.t("Rearmar mi última búsqueda")}
						</span>
					</div>
				`
			: html``;

	const redemptionMilesCheckboxTemplate = () =>
		showRedemptionMiles()
			? html`
					<div class="dg-redemption-checkbox">
						<input
							type="checkbox"
							id=${REDEMPTION_MILES_ID}
							autocomplete="off"
							?checked=${props.currentSearchManager.currentSearch.useAmericanMiles}
							@click=${() =>
								props.currentSearchManager.setUseAmericanMiles(
									!props.currentSearchManager.currentSearch.useAmericanMiles
								)}
						/>
						<label class="ml-2 text-n-blue" for=${REDEMPTION_MILES_ID}
							>${i18next.t("Use AAdvantage® miles")}</label
						>
					</div>
				`
			: html``;

	const mobileExtraFieldsTemplate = () =>
		props.currentSearchManager.currentSearch.originCity && props.currentSearchManager.currentSearch.destinationCity
			? html` ${dateSelector.htmlTemplate()}${passengerSelector.htmlTemplate()} `
			: "";

	const mobileFlightsTabTemplate = () => html`
		<div class="flex flex-col gap-4">
			<div class="mt-6 grid grid-cols-2 gap-4">
				<div class="col-span-2">${radioToggle.htmlTemplate()}</div>
				<div class="col-span-2">${stationSelector.htmlTemplate()}</div>
				${mobileExtraFieldsTemplate()}
			</div>
			${promoCodeBox.htmlTemplate()} ${redemptionMilesCheckboxTemplate()}
			<div class="flex flex-col gap-4">${reloadLinkTemplate()}${button.htmlTemplate()}</div>
		</div>
	`;

	const desktopFlightsTabTemplate = () => html`
		<div class="mt-4 flex flex-col gap-4">
			<div class="mt-2">${radioToggle.htmlTemplate()}</div>
			<div class="grid w-full grid-cols-[2fr_1fr_1fr_1fr] gap-4">
				${stationSelector.htmlTemplate()} ${dateSelector.htmlTemplate()} ${passengerSelector.htmlTemplate()}
			</div>
			${redemptionMilesCheckboxTemplate()}
			<div
				class=${classNames("flex items-center", {
					"justify-between": showReloadLink,
					"justify-end": !showReloadLink,
				})}
			>
				${reloadLinkTemplate()}
				<div class="flex items-end justify-center gap-4">
					${promoCodeBox.htmlTemplate()} ${button.htmlTemplate()}
				</div>
			</div>
		</div>
	`;

	const htmlTemplate = () =>
		windowSize.size === "XS" || windowSize.size === "SM"
			? html`<div>${mobileFlightsTabTemplate()}</div>`
			: html`<div>${desktopFlightsTabTemplate()}</div> `;

	return { htmlTemplate };
};
